import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import shallow from "zustand/shallow";
import { useMount } from "react-use";

import { useClassroomDetailsQuery } from "@/query/classroomDetailsQuery";
import { getClassroomStore } from "@/stores/classroomStore";
import { ClassroomDetails } from "@/stores/classroomStore_I";
import ErrorBoundary from "@/components/ErrorBoundary";
import { loadDynamicRemote } from "@/utils/loadDynamicRemote";
import { getHallPassMicrofrontendUrl } from "js/utils/getMicrofrontendUrl";
import useStore from "stores/index";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { CLASSROOM_TYPES } from "types/classrooms";
import Nav from "../Nav";
import { TabBar } from "../TabBar";
import ErrorFallback from "./ErrorFallback";
import LoadingFallback from "./LoadingFallback";

type HallwayPassClassroom = Pick<ClassroomDetails, "id" | "name" | "type" | "sourceId" | "personalisation" | "description">;

const classroomFields = ["id", "name", "type", "sourceId", "personalisation", "description"] as const;

function toHallwayPassClassroom(classroom: ClassroomDetails | Record<string, never>): HallwayPassClassroom | undefined {
	const picked = _.pick(classroom, classroomFields);
	if (_.isEmpty(picked) || !picked.id || !picked.name || !picked.type) {
		return undefined;
	}
	return picked as HallwayPassClassroom;
}

const HallwayPass = (): JSX.Element => {
	const { i18n } = useTranslation();
	const params = useParams();
	const classId = params.classId ?? "";

	const classroomStore = useStore(
		(state) => ({
			getClassroomV2: state.classroom.getClassroomV2,
			fetchClassrooms: state.classroom.fetchClassrooms,
		}),
		shallow
	);

	const selectedClassroomStore = useSelectedClassroomStore(
		(state) => ({
			setData: state.setData,
		}),
		shallow
	);

	const [classroom, setClassroom] = useState<HallwayPassClassroom | undefined>(() =>
		toHallwayPassClassroom(classroomStore.getClassroomV2(classId))
	);

	const classroomQuery = useClassroomDetailsQuery(
		classId,
		true,
		true,
		false,
		classroom?.type ?? "CLASSROOM",
		classroom?.sourceId ?? "",
		useUserSessionStore.getState().getTeacherId() ?? "",
		{
			enabled: !!classroom,
			select: (data: { classroom: ClassroomDetails }) => data.classroom,
		}
	);

	useEffect(() => {
		// The query hook's declared type ignores `select`; at runtime `data` is the classroom.
		const details = classroomQuery.data as ClassroomDetails | undefined;
		if (!details?.id || !classroom?.personalisation) {
			return;
		}
		const next = toHallwayPassClassroom(details);
		if (!next) {
			return;
		}
		setClassroom({ ...next, personalisation: classroom.personalisation });
	}, [classroomQuery.data, classroom?.personalisation]);

	useMount(async () => {
		if (classroom) {
			return;
		}
		await classroomStore.fetchClassrooms();
		const nextClassroom = classroomStore.getClassroomV2(classId);
		const picked = toHallwayPassClassroom(nextClassroom);
		if (!picked) {
			return;
		}
		setClassroom(picked);
		selectedClassroomStore.setData({
			id: nextClassroom.id,
			stableIdV1: nextClassroom.stableIdV1,
			sourceId: nextClassroom.sourceId,
			name: nextClassroom.name,
			students: (nextClassroom.students ?? []).map((student) => ({
				id: student.id,
				stableIdV1: student.stableIdV1,
				firstName: student.firstName,
				lastName: student.lastName,
				username: student.username,
				pinned: student.pinned,
				qoriaUserId: student.qoriaUserId ?? "",
			})),
			personalisation: nextClassroom.personalisation
				? {
						...nextClassroom.personalisation,
						displayName: nextClassroom.personalisation.displayName ?? nextClassroom.name,
					}
				: null,
			qoriaUserId: null,
		});
	});

	const classroomType = classroom?.type.toLowerCase() === CLASSROOM_TYPES.CLASSROOM ? "CLASS" : "GROUP";
	const hallPassModuleRemoteUrl = useMemo(
		() => getHallPassMicrofrontendUrl(window.location.hostname) + "?nocache=" + String(Date.now()),
		[]
	);

	const DynamicRemoteApp = useMemo(
		() => lazy(() => loadDynamicRemote(hallPassModuleRemoteUrl, "TeacherHallPass", "./app", "vite", "esm")),
		[hallPassModuleRemoteUrl]
	);

	const hallwayPassLocale = i18n.resolvedLanguage ?? i18n.language;
	const isClassActive = useStore((state) => getClassroomStore(state).isClassSessionActiveV2(classId));

	return (
		<Box>
			<Nav
				selectedClass={
					classroom
						? {
								description: classroom.description ?? "",
								name: classroom.name,
								personalisation: classroom.personalisation?.displayName
									? { displayName: classroom.personalisation.displayName }
									: undefined,
								type: classroom.type,
							}
						: undefined
				}
			/>
			<TabBar isActive={isClassActive} />
			{!!classroom && (
				<ErrorBoundary fallback={<ErrorFallback />}>
					<Suspense fallback={<LoadingFallback />}>
						<DynamicRemoteApp
							key={hallwayPassLocale}
							classId={classId}
							classType={classroomType}
							applianceId={localStorage.getItem("DEVICE")}
							token={localStorage.getItem("TOKEN")}
							locale={hallwayPassLocale}
						/>
					</Suspense>
				</ErrorBoundary>
			)}
		</Box>
	);
};

export default HallwayPass;
